import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams } from 'types';
import { PaginatedHoldReportList } from 'types/generated';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseAthletesOnHold {
  date: string;
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useAthletesOnHold({
  date,
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
}: IUseAthletesOnHold): UseInfiniteQueryResult<AxiosResponse<PaginatedHoldReportList>> {
  const fetchAthletesOnHold = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedHoldReportList>> => {
      const params = pageParam;

      params.date = date;

      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
      }

      return client.get(`reports/athletes-on-hold?${encodeUrlParams(params)}`);
    },
    [limit, date, ordering],
  );

  return useInfiniteQuery(['athletes-on-hold', date, ordering, csvExport], fetchAthletesOnHold, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: enabled && date !== null,
    staleTime: 86400000, // 1 day
  });
}

export default useAthletesOnHold;
